import actions from './actions';
import authActions from '../auth/actions';

const initState = {
  // Subcontas por professionalUid. Suporta secretária com N profissionais.
  // { [professionalUid]: { subaccountId, walletId, kycStatus, active, createdAt } }
  accounts: {},
  settingUpAccount: false,
  fetchingAccountStatus: false,
  fetchingZipCode: false,
  zipData: null,
  fees: null,
  loadingFees: false,
  charges: {},
  fetchingCharges: false,
  cancellingCharge: false,
  refundingCharge: false,
  discardingAccount: false,
  error: null,
  checkoutInfo: null,
  fetchingCheckoutInfo: false,
  checkoutError: null,
  checkoutCpfValidated: false,
  cpfValidatedToken: null,
  validatingCheckoutCpf: false,
  checkoutCpfError: null,
  creatingCheckoutCharge: false,
  checkoutCharge: null,
  checkoutChargeError: null,
  creatingCheckoutPreloadCharge: false,
  checkoutPreloadChargeError: null,
  // { [itemKey]: { loading, installments, error } }
  paymentSimulations: {},
  // { balance, pending, overdue }
  accountBalance: null,
  fetchingBalance: false,
  // Fiscal config
  fiscalInfo: null,
  fetchingFiscalInfo: false,
  savingFiscalInfo: false,
  municipalOptions: null,
  fetchingMunicipalOptions: false,
  municipalServices: [],
  fetchingMunicipalServices: false,
  // Invoice defaults
  invoiceDefaults: null,
  fetchingInvoiceDefaults: false,
  savingInvoiceDefaults: false,
  // Notification defaults
  notificationDefaults: null,
  messagingFees: null,
  fetchingNotificationDefaults: false,
  savingNotificationDefaults: false,
  applyingNotificationDefaults: false,
  // { processed, totalCount, succeeded, failed }
  applyNotificationDefaultsProgress: null,
  // Customers (clientes Asaas da subconta)
  asaasCustomers: [],
  asaasCustomersTotal: 0,
  fetchingAsaasCustomers: false,
  syncingAsaasCustomerId: null,
  customerNotifications: null,
  fetchingCustomerNotifications: false,
  updatingCustomerNotifications: false,
  updateCustomerNotificationsSuccess: false,
  // Account fees (tela de taxas)
  accountFees: null,
  fetchingAccountFees: false,
  accountFeesError: false,
  // Commercial Info (PJ Upgrade)
  updatingCommercialInfo: false,
  // Invoice patient
  invoicePatient: null,
  invoicePatientId: null,
  fetchingInvoicePatient: false,
  // Invoice procedures (descrição dinâmica da NF)
  invoiceProcedures: null,
  fetchingInvoiceProcedures: false,
  // Invoice operations
  creatingInvoice: false,
  createInvoiceSuccess: false,
  invoiceDetail: null,
  fetchingInvoice: false,
  invoices: [],
  fetchingInvoices: false,
  invoicesTotal: 0,
  // Transfers (Saque)
  transfers: [],
  fetchingTransfers: false,
  // Chave PIX de saque (Segurança)
  savingWithdrawPixKey: false,
  withdrawPixKeyLookup: { loading: false, result: null, error: null },
  withdrawKeyHistory: [],
  fetchingWithdrawKeyHistory: false,
  // Financial Transactions (Extrato)
  financialTransactions: [],
  financialTransactionsTotal: 0,
  fetchingFinancialTransactions: false,
  creatingTransfer: false,
  createTransferSuccess: false,
  createTransferError: null,
  // Check Refund Status
  checkingRefundStatus: false,
  refundStatusResult: null,
  refundStatusError: null,
  // KYC Documents
  pendingDocuments: null,
  detailedStatus: null,
  rejectReasons: null,
  // Commercial Info Expiration
  commercialInfoExpiration: null,
  // Anticipations (Antecipação de Recebíveis)
  anticipationSimulation: null,
  simulatingAnticipation: false,
  anticipationSimulationError: null,
  requestingAnticipation: false,
  requestAnticipationSuccess: false,
  requestAnticipationError: null,
  anticipations: [],
  anticipationsTotal: 0,
  fetchingAnticipations: false,
  anticipationLimits: null,
  fetchingAnticipationLimits: false,
  cancellingAnticipation: false,
  // Acesso de secretárias (walletAccess)
  accountsLoaded: false,
  // Carteira selecionada por secretária com acesso concedido (null = a própria)
  walletOwnerId: null,
  savingWalletAccess: false,
  walletAudit: { entries: [], fetching: false },
};

// Campos presos a uma carteira. Ficam de fora: `accounts`/`accountsLoaded` (o
// roteamento da secretária depende deles), a área do dono, o KYC do próprio
// usuário, `charges` (o canal é da clínica e as telas filtram por profissional)
// e o que é consumido fora do módulo (`fees`, `paymentSimulations`, `zipData`,
// `checkout*`).
const WALLET_SCOPED_KEYS = [
  'accountBalance', 'fetchingBalance',
  'fiscalInfo', 'fetchingFiscalInfo', 'savingFiscalInfo',
  'municipalOptions', 'fetchingMunicipalOptions',
  'municipalServices', 'fetchingMunicipalServices',
  'invoiceDefaults', 'fetchingInvoiceDefaults', 'savingInvoiceDefaults',
  'notificationDefaults', 'messagingFees',
  'fetchingNotificationDefaults', 'savingNotificationDefaults',
  'applyingNotificationDefaults', 'applyNotificationDefaultsProgress',
  'asaasCustomers', 'asaasCustomersTotal', 'fetchingAsaasCustomers',
  'syncingAsaasCustomerId',
  'customerNotifications', 'fetchingCustomerNotifications',
  'updatingCustomerNotifications', 'updateCustomerNotificationsSuccess',
  'accountFees', 'fetchingAccountFees', 'accountFeesError',
  'invoicePatient', 'invoicePatientId', 'fetchingInvoicePatient',
  'invoiceProcedures', 'fetchingInvoiceProcedures',
  'creatingInvoice', 'createInvoiceSuccess',
  'invoiceDetail', 'fetchingInvoice',
  'invoices', 'fetchingInvoices', 'invoicesTotal',
  'transfers', 'fetchingTransfers',
  'financialTransactions', 'financialTransactionsTotal',
  'fetchingFinancialTransactions',
  'creatingTransfer', 'createTransferSuccess', 'createTransferError',
  'checkingRefundStatus', 'refundStatusResult', 'refundStatusError',
  'cancellingCharge', 'refundingCharge',
  'anticipationSimulation', 'simulatingAnticipation', 'anticipationSimulationError',
  'requestingAnticipation', 'requestAnticipationSuccess', 'requestAnticipationError',
  'anticipations', 'anticipationsTotal', 'fetchingAnticipations',
  'anticipationLimits', 'fetchingAnticipationLimits', 'cancellingAnticipation',
];

const walletScopedState = {};
WALLET_SCOPED_KEYS.forEach((key) => {
  walletScopedState[key] = initState[key];
});

export default function asaasReducer(state = initState, action) {
  switch (action.type) {
    case authActions.CLEAR_STATES:
      return {
        ...initState,
      };
    case actions.LOAD_ASAAS_ACCOUNT_SUCCESS: {
      return {
        ...state,
        settingUpAccount: false,
        accountsLoaded: true,
        accounts: {
          ...action.payload,
        },
      };
    }
    case actions.SELECT_WALLET_OWNER: {
      const nextOwnerId = action.payload || null;
      if (nextOwnerId === state.walletOwnerId) return state;
      return {
        ...state,
        ...walletScopedState,
        walletOwnerId: nextOwnerId,
      };
    }
    case actions.SAVE_WALLET_ACCESS_REQUEST:
      return {
        ...state,
        savingWalletAccess: true,
      };
    case actions.SAVE_WALLET_ACCESS_SUCCESS: {
      const { ownerUid, walletAccess } = action.payload || {};
      const account = ownerUid ? state.accounts?.[ownerUid] : null;
      return {
        ...state,
        savingWalletAccess: false,
        accounts: account
          ? { ...state.accounts, [ownerUid]: { ...account, walletAccess } }
          : state.accounts,
      };
    }
    case actions.SAVE_WALLET_ACCESS_ERROR:
      return {
        ...state,
        savingWalletAccess: false,
      };
    case actions.LOAD_WALLET_AUDIT_REQUEST:
      return {
        ...state,
        walletAudit: { entries: [], fetching: true },
      };
    case actions.LOAD_WALLET_AUDIT_SUCCESS:
      return {
        ...state,
        walletAudit: { entries: action.payload, fetching: false },
      };
    case actions.LOAD_WALLET_AUDIT_ERROR:
      return {
        ...state,
        walletAudit: { entries: [], fetching: false },
      };
    case actions.SETUP_ASAAS_ACCOUNT_REQUEST:
      return {
        ...state,
        settingUpAccount: true,
        error: null,
      };
    // case actions.SETUP_ASAAS_ACCOUNT_SUCCESS: {
    //   const { professionalUid, accountData } = action.payload;
    //   return {
    //     ...state,
    //     settingUpAccount: false,
    //     accounts: {
    //       ...state.accounts,
    //       [professionalUid]: accountData,
    //     },
    //   };
    // }
    case actions.SETUP_ASAAS_ACCOUNT_ERROR:
      return {
        ...state,
        settingUpAccount: false,
        error: action.payload,
      };
    case actions.DISCARD_REJECTED_ACCOUNT_REQUEST:
      return {
        ...state,
        discardingAccount: true,
        error: null,
      };
    case actions.DISCARD_REJECTED_ACCOUNT_SUCCESS:
      return {
        ...state,
        discardingAccount: false,
      };
    case actions.DISCARD_REJECTED_ACCOUNT_ERROR:
      return {
        ...state,
        discardingAccount: false,
        error: action.payload,
      };
    case actions.GET_ASAAS_ACCOUNT_STATUS_REQUEST:
      return {
        ...state,
        fetchingAccountStatus: true,
        error: null,
      };
    case actions.GET_ASAAS_ACCOUNT_STATUS_SUCCESS: {
      const {
        professionalUid, kycStatus, kycCheckedAt,
        detailedStatus, pendingDocuments, rejectReasons,
        commercialInfoExpiration,
      } = action.payload;
      return {
        ...state,
        fetchingAccountStatus: false,
        detailedStatus: detailedStatus || null,
        pendingDocuments: pendingDocuments || null,
        rejectReasons: rejectReasons || null,
        commercialInfoExpiration: commercialInfoExpiration || null,
        accounts: {
          ...state.accounts,
          [professionalUid]: {
            ...state.accounts[professionalUid],
            kycStatus,
            kycCheckedAt,
            commercialInfoExpiration: commercialInfoExpiration || null,
          },
        },
      };
    }
    case actions.GET_ASAAS_ACCOUNT_STATUS_ERROR:
      return {
        ...state,
        fetchingAccountStatus: false,
        error: action.payload,
      };
    case actions.SEARCH_ZIP_CODE_REQUEST:
      return {
        ...state,
        fetchingZipCode: true,
        zipData: null,
      };
    case actions.SEARCH_ZIP_CODE_SUCCESS:
      return {
        ...state,
        fetchingZipCode: false,
        zipData: action.payload,
      };
    case actions.LOAD_ASAAS_FEES_REQUEST:
      return {
        ...state,
        loadingFees: true,
      };
    case actions.LOAD_ASAAS_FEES_SUCCESS:
      return {
        ...state,
        loadingFees: false,
        fees: action.payload,
      };
    case actions.LOAD_ASAAS_FEES_ERROR:
      return {
        ...state,
        loadingFees: false,
      };
    case actions.LOAD_ASAAS_CHARGES_REQUEST:
      return {
        ...state,
        fetchingCharges: true,
      };
    case actions.ASAAS_CHARGES_UPDATED:
      return {
        ...state,
        fetchingCharges: false,
        charges: action.payload,
      };
    case actions.LOAD_ASAAS_CHARGES_ERROR:
      return {
        ...state,
        fetchingCharges: false,
        error: action.payload,
      };
    case actions.CANCEL_ASAAS_CHARGE_REQUEST:
      return {
        ...state,
        cancellingCharge: true,
        error: null,
      };
    case actions.CANCEL_ASAAS_CHARGE_SUCCESS:
      // Só as parcelas restantes: o listener do RTDB traz o carnê atualizado
      if (action.payload.mode === 'remaining') {
        return { ...state, cancellingCharge: false };
      }
      return {
        ...state,
        cancellingCharge: false,
        charges: {
          ...state.charges,
          [action.payload.chargeKey]: {
            ...state.charges[action.payload.chargeKey],
            status: 'CANCELLED',
          },
        },
      };
    case actions.CANCEL_ASAAS_CHARGE_ERROR:
      return {
        ...state,
        cancellingCharge: false,
        error: action.payload,
      };
    case actions.REFUND_ASAAS_CHARGE_REQUEST:
      return {
        ...state,
        refundingCharge: true,
        error: null,
      };
    case actions.REFUND_ASAAS_CHARGE_SUCCESS:
      return {
        ...state,
        refundingCharge: false,
        refundStatusResult: null,
      };
    case actions.REFUND_ASAAS_CHARGE_ERROR:
      return {
        ...state,
        refundingCharge: false,
        error: action.payload,
      };
    case actions.CHECK_REFUND_STATUS_REQUEST:
      return {
        ...state,
        checkingRefundStatus: true,
        refundStatusResult: null,
        refundStatusError: null,
      };
    case actions.CHECK_REFUND_STATUS_SUCCESS:
      return {
        ...state,
        checkingRefundStatus: false,
        refundStatusResult: action.payload,
      };
    case actions.CHECK_REFUND_STATUS_ERROR:
      return {
        ...state,
        checkingRefundStatus: false,
        refundStatusError: action.payload,
      };
    case actions.GET_CHECKOUT_INFO_REQUEST:
      // Polling silencioso não reseta a tela nem a validação de CPF
      if (action.payload?.silent) return state;
      return {
        ...state,
        fetchingCheckoutInfo: true,
        checkoutError: null,
        checkoutInfo: null,
        checkoutCpfValidated: false,
        cpfValidatedToken: null,
      };
    case actions.GET_CHECKOUT_INFO_SUCCESS:
      return {
        ...state,
        fetchingCheckoutInfo: false,
        checkoutInfo: action.payload,
      };
    case actions.GET_CHECKOUT_INFO_ERROR:
      return {
        ...state,
        fetchingCheckoutInfo: false,
        checkoutError: action.payload,
      };
    case actions.VALIDATE_CHECKOUT_CPF_REQUEST:
      return {
        ...state,
        validatingCheckoutCpf: true,
        checkoutCpfError: null,
      };
    case actions.VALIDATE_CHECKOUT_CPF_SUCCESS:
      return {
        ...state,
        validatingCheckoutCpf: false,
        checkoutCpfValidated: true,
        cpfValidatedToken: action.payload?.cpfValidatedToken || null,
      };
    case actions.VALIDATE_CHECKOUT_CPF_ERROR:
      return {
        ...state,
        validatingCheckoutCpf: false,
        checkoutCpfError: action.payload,
      };
    case actions.CLEAR_CHECKOUT_CHARGE:
      return {
        ...state,
        checkoutCharge: null,
        checkoutChargeError: null,
      };
    case actions.CREATE_PUBLIC_CHARGE_REQUEST:
      return {
        ...state,
        creatingCheckoutCharge: true,
        checkoutChargeError: null,
        checkoutCharge: null,
      };
    case actions.CREATE_PUBLIC_CHARGE_SUCCESS:
      return {
        ...state,
        creatingCheckoutCharge: false,
        checkoutCharge: action.payload,
      };
    case actions.CREATE_PUBLIC_CHARGE_ERROR:
      return {
        ...state,
        creatingCheckoutCharge: false,
        checkoutChargeError: action.payload,
      };
    case actions.CREATE_CHECKOUT_CHARGE_REQUEST:
      return {
        ...state,
        creatingCheckoutPreloadCharge: true,
        checkoutPreloadChargeError: null,
      };
    case actions.CREATE_CHECKOUT_CHARGE_SUCCESS: {
      const { billingType, chargeData } = action.payload;
      return {
        ...state,
        creatingCheckoutPreloadCharge: false,
        checkoutInfo: {
          ...state.checkoutInfo,
          chargesData: {
            ...state.checkoutInfo.chargesData,
            [billingType]: chargeData,
          },
        },
      };
    }
    case actions.CREATE_CHECKOUT_CHARGE_ERROR:
      return {
        ...state,
        creatingCheckoutPreloadCharge: false,
        checkoutPreloadChargeError: action.payload,
      };
    case actions.SIMULATE_ASAAS_PAYMENT_REQUEST:
      return {
        ...state,
        paymentSimulations: {
          ...state.paymentSimulations,
          [action.payload.itemKey]: {
            loading: true,
            installments: null,
            pix: null,
            boleto: null,
            pixByInstallment: null,
            boletoByInstallment: null,
            error: null,
          },
        },
      };
    case actions.SIMULATE_ASAAS_PAYMENT_SUCCESS: {
      return {
        ...state,
        paymentSimulations: {
          ...state.paymentSimulations,
          [action.payload.itemKey]: {
            loading: false,
            installments: action.payload.installments,
            pix: action.payload.pix,
            boleto: action.payload.boleto,
            pixByInstallment: action.payload.pixByInstallment,
            boletoByInstallment: action.payload.boletoByInstallment,
            error: null,
          },
        },
      };
    }
    case actions.SIMULATE_ASAAS_PAYMENT_ERROR:
      return {
        ...state,
        paymentSimulations: {
          ...state.paymentSimulations,
          [action.payload.itemKey]: {
            loading: false,
            installments: null,
            pix: null,
            boleto: null,
            pixByInstallment: null,
            boletoByInstallment: null,
            error: action.payload.error,
          },
        },
      };
    case actions.SIMULATE_ASAAS_PAYMENT_CLEAR: {
      const { [action.payload]: _, ...rest } = state.paymentSimulations;
      return {
        ...state,
        paymentSimulations: rest,
      };
    }
    case actions.LOAD_ASAAS_BALANCE_REQUEST:
      return {
        ...state,
        fetchingBalance: true,
      };
    case actions.LOAD_ASAAS_BALANCE_SUCCESS:
      return {
        ...state,
        fetchingBalance: false,
        accountBalance: action.payload,
      };
    case actions.LOAD_ASAAS_BALANCE_ERROR:
      return {
        ...state,
        fetchingBalance: false,
      };
    // Fiscal Config
    case actions.GET_FISCAL_INFO_REQUEST:
      return {
        ...state,
        fetchingFiscalInfo: true,
      };
    case actions.GET_FISCAL_INFO_SUCCESS:
      return {
        ...state,
        fetchingFiscalInfo: false,
        fiscalInfo: action.payload,
      };
    case actions.GET_FISCAL_INFO_ERROR:
      return {
        ...state,
        fetchingFiscalInfo: false,
      };
    case actions.SAVE_FISCAL_INFO_REQUEST:
      return {
        ...state,
        savingFiscalInfo: true,
      };
    case actions.SAVE_FISCAL_INFO_SUCCESS:
      return {
        ...state,
        savingFiscalInfo: false,
        fiscalInfo: action.payload,
      };
    case actions.SAVE_FISCAL_INFO_ERROR:
      return {
        ...state,
        savingFiscalInfo: false,
      };
    case actions.GET_MUNICIPAL_OPTIONS_REQUEST:
      return {
        ...state,
        fetchingMunicipalOptions: true,
      };
    case actions.GET_MUNICIPAL_OPTIONS_SUCCESS:
      return {
        ...state,
        fetchingMunicipalOptions: false,
        municipalOptions: action.payload,
      };
    case actions.GET_MUNICIPAL_OPTIONS_ERROR:
      return {
        ...state,
        fetchingMunicipalOptions: false,
      };
    case actions.LIST_MUNICIPAL_SERVICES_REQUEST:
      return {
        ...state,
        fetchingMunicipalServices: true,
        municipalServices: [],
      };
    case actions.LIST_MUNICIPAL_SERVICES_SUCCESS:
      return {
        ...state,
        fetchingMunicipalServices: false,
        municipalServices: action.payload,
      };
    case actions.LIST_MUNICIPAL_SERVICES_ERROR:
      return {
        ...state,
        fetchingMunicipalServices: false,
      };
    // Invoice Defaults
    case actions.GET_INVOICE_DEFAULTS_REQUEST:
      return {
        ...state,
        fetchingInvoiceDefaults: true,
      };
    case actions.GET_INVOICE_DEFAULTS_SUCCESS:
      return {
        ...state,
        fetchingInvoiceDefaults: false,
        invoiceDefaults: action.payload,
      };
    case actions.GET_INVOICE_DEFAULTS_ERROR:
      return {
        ...state,
        fetchingInvoiceDefaults: false,
      };
    case actions.SAVE_INVOICE_DEFAULTS_REQUEST:
      return {
        ...state,
        savingInvoiceDefaults: true,
      };
    case actions.SAVE_INVOICE_DEFAULTS_SUCCESS:
      return {
        ...state,
        savingInvoiceDefaults: false,
        invoiceDefaults: action.payload,
      };
    case actions.SAVE_INVOICE_DEFAULTS_ERROR:
      return {
        ...state,
        savingInvoiceDefaults: false,
      };
    // Notification Defaults
    case actions.GET_NOTIFICATION_DEFAULTS_REQUEST:
      return {
        ...state,
        fetchingNotificationDefaults: true,
      };
    case actions.GET_NOTIFICATION_DEFAULTS_SUCCESS:
      return {
        ...state,
        fetchingNotificationDefaults: false,
        notificationDefaults: action.payload.notificationDefaults,
        messagingFees: action.payload.messagingFees,
      };
    case actions.GET_NOTIFICATION_DEFAULTS_ERROR:
      return {
        ...state,
        fetchingNotificationDefaults: false,
      };
    case actions.SAVE_NOTIFICATION_DEFAULTS_REQUEST:
      return {
        ...state,
        savingNotificationDefaults: true,
      };
    case actions.SAVE_NOTIFICATION_DEFAULTS_SUCCESS:
      return {
        ...state,
        savingNotificationDefaults: false,
        notificationDefaults: action.payload,
      };
    case actions.SAVE_NOTIFICATION_DEFAULTS_ERROR:
      return {
        ...state,
        savingNotificationDefaults: false,
      };
    case actions.APPLY_NOTIFICATION_DEFAULTS_REQUEST:
      return {
        ...state,
        applyingNotificationDefaults: true,
        applyNotificationDefaultsProgress: null,
      };
    case actions.APPLY_NOTIFICATION_DEFAULTS_PROGRESS:
      return {
        ...state,
        applyNotificationDefaultsProgress: action.payload,
      };
    case actions.APPLY_NOTIFICATION_DEFAULTS_SUCCESS:
      return {
        ...state,
        applyingNotificationDefaults: false,
        applyNotificationDefaultsProgress: null,
      };
    case actions.APPLY_NOTIFICATION_DEFAULTS_ERROR:
      return {
        ...state,
        applyingNotificationDefaults: false,
        applyNotificationDefaultsProgress: null,
      };
    // Customers
    case actions.LIST_ASAAS_CUSTOMERS_REQUEST:
      return {
        ...state,
        fetchingAsaasCustomers: true,
      };
    case actions.LIST_ASAAS_CUSTOMERS_SUCCESS:
      return {
        ...state,
        fetchingAsaasCustomers: false,
        asaasCustomers: action.payload.data || [],
        asaasCustomersTotal: action.payload.totalCount || 0,
      };
    case actions.LIST_ASAAS_CUSTOMERS_ERROR:
      return {
        ...state,
        fetchingAsaasCustomers: false,
      };
    case actions.SYNC_ASAAS_CUSTOMER_REQUEST:
      return {
        ...state,
        syncingAsaasCustomerId: action.payload.customerId,
      };
    case actions.SYNC_ASAAS_CUSTOMER_SUCCESS: {
      const syncedCustomer = action.payload.customer;
      const asaasCustomers = syncedCustomer
        ? state.asaasCustomers.map((customer) => (customer.id === syncedCustomer.id
          ? { ...customer, ...syncedCustomer }
          : customer))
        : state.asaasCustomers;
      return {
        ...state,
        syncingAsaasCustomerId: null,
        asaasCustomers,
      };
    }
    case actions.SYNC_ASAAS_CUSTOMER_ERROR:
      return {
        ...state,
        syncingAsaasCustomerId: null,
      };
    case actions.GET_CUSTOMER_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        fetchingCustomerNotifications: true,
        customerNotifications: null,
      };
    case actions.GET_CUSTOMER_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        fetchingCustomerNotifications: false,
        customerNotifications: action.payload,
      };
    case actions.GET_CUSTOMER_NOTIFICATIONS_ERROR:
      return {
        ...state,
        fetchingCustomerNotifications: false,
      };
    case actions.UPDATE_CUSTOMER_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        updatingCustomerNotifications: true,
        updateCustomerNotificationsSuccess: false,
      };
    case actions.UPDATE_CUSTOMER_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        updatingCustomerNotifications: false,
        updateCustomerNotificationsSuccess: true,
        asaasCustomers: typeof action.payload.notificationDisabled === 'boolean'
          ? state.asaasCustomers.map((customer) => (customer.id === action.payload.customerId
            ? { ...customer, notificationDisabled: action.payload.notificationDisabled }
            : customer))
          : state.asaasCustomers,
      };
    case actions.UPDATE_CUSTOMER_NOTIFICATIONS_ERROR:
      return {
        ...state,
        updatingCustomerNotifications: false,
      };
    // Account Fees
    case actions.GET_ACCOUNT_FEES_REQUEST:
      return {
        ...state,
        fetchingAccountFees: true,
        accountFeesError: false,
      };
    case actions.GET_ACCOUNT_FEES_SUCCESS:
      return {
        ...state,
        fetchingAccountFees: false,
        accountFees: action.payload,
      };
    case actions.GET_ACCOUNT_FEES_ERROR:
      return {
        ...state,
        fetchingAccountFees: false,
        accountFeesError: true,
      };
    // Commercial Info (PJ Upgrade)
    case actions.UPDATE_COMMERCIAL_INFO_REQUEST:
      return {
        ...state,
        updatingCommercialInfo: true,
      };
    case actions.UPDATE_COMMERCIAL_INFO_SUCCESS:
      return {
        ...state,
        updatingCommercialInfo: false,
      };
    case actions.UPDATE_COMMERCIAL_INFO_ERROR:
      return {
        ...state,
        updatingCommercialInfo: false,
      };
    // Invoice Patient
    case actions.FETCH_INVOICE_PATIENT_REQUEST:
      return {
        ...state,
        fetchingInvoicePatient: true,
        invoicePatient: null,
        invoicePatientId: action.payload.patientId,
      };
    case actions.FETCH_INVOICE_PATIENT_SUCCESS:
      return {
        ...state,
        fetchingInvoicePatient: false,
        invoicePatient: action.payload,
      };
    case actions.FETCH_INVOICE_PATIENT_ERROR:
      return {
        ...state,
        fetchingInvoicePatient: false,
      };
    // Invoice Procedures
    case actions.FETCH_INVOICE_PROCEDURES_REQUEST:
      return {
        ...state,
        fetchingInvoiceProcedures: true,
        invoiceProcedures: null,
      };
    case actions.FETCH_INVOICE_PROCEDURES_SUCCESS:
      return {
        ...state,
        fetchingInvoiceProcedures: false,
        invoiceProcedures: action.payload,
      };
    case actions.FETCH_INVOICE_PROCEDURES_ERROR:
      return {
        ...state,
        fetchingInvoiceProcedures: false,
      };
    // Invoice Operations
    case actions.CREATE_INVOICE_REQUEST:
      return {
        ...state,
        creatingInvoice: true,
        createInvoiceSuccess: false,
      };
    case actions.CREATE_INVOICE_SUCCESS:
      return {
        ...state,
        creatingInvoice: false,
        createInvoiceSuccess: true,
      };
    case actions.CREATE_INVOICE_ERROR:
      return {
        ...state,
        creatingInvoice: false,
        createInvoiceSuccess: false,
      };
    case actions.RESET_CREATE_INVOICE:
      return {
        ...state,
        createInvoiceSuccess: false,
      };
    case actions.GET_INVOICE_REQUEST:
      return {
        ...state,
        fetchingInvoice: true,
        invoiceDetail: null,
      };
    case actions.GET_INVOICE_SUCCESS:
      return {
        ...state,
        fetchingInvoice: false,
        invoiceDetail: action.payload,
      };
    case actions.GET_INVOICE_ERROR:
      return {
        ...state,
        fetchingInvoice: false,
      };
    case actions.LIST_INVOICES_REQUEST:
      return {
        ...state,
        fetchingInvoices: true,
      };
    case actions.LIST_INVOICES_SUCCESS:
      return {
        ...state,
        fetchingInvoices: false,
        invoices: action.payload.data,
        invoicesTotal: action.payload.totalCount,
      };
    case actions.LIST_INVOICES_ERROR:
      return {
        ...state,
        fetchingInvoices: false,
      };
    // Transfers (Saque)
    case actions.CREATE_TRANSFER_REQUEST:
      return {
        ...state,
        creatingTransfer: true,
        createTransferSuccess: false,
        createTransferError: null,
      };
    case actions.CREATE_TRANSFER_SUCCESS:
      return {
        ...state,
        creatingTransfer: false,
        createTransferSuccess: true,
      };
    case actions.CREATE_TRANSFER_ERROR:
      return {
        ...state,
        creatingTransfer: false,
        createTransferError: action.payload,
      };
    case actions.CREATE_TRANSFER_RESET:
      return {
        ...state,
        creatingTransfer: false,
        createTransferSuccess: false,
        createTransferError: null,
      };
    case actions.LIST_TRANSFERS_REQUEST:
      return {
        ...state,
        fetchingTransfers: true,
      };
    case actions.LIST_TRANSFERS_SUCCESS:
      return {
        ...state,
        fetchingTransfers: false,
        transfers: action.payload,
      };
    case actions.LIST_TRANSFERS_ERROR:
      return {
        ...state,
        fetchingTransfers: false,
      };
    // Chave PIX de saque (Segurança)
    case actions.SAVE_WITHDRAW_PIX_KEY_REQUEST:
      return {
        ...state,
        savingWithdrawPixKey: true,
      };
    case actions.SAVE_WITHDRAW_PIX_KEY_SUCCESS:
      return {
        ...state,
        savingWithdrawPixKey: false,
        withdrawPixKeyLookup: { loading: false, result: null, error: null },
      };
    case actions.SAVE_WITHDRAW_PIX_KEY_ERROR:
      return {
        ...state,
        savingWithdrawPixKey: false,
      };
    case actions.LOOKUP_WITHDRAW_PIX_KEY_REQUEST:
      return {
        ...state,
        withdrawPixKeyLookup: { loading: true, result: null, error: null },
      };
    case actions.LOOKUP_WITHDRAW_PIX_KEY_SUCCESS:
      return {
        ...state,
        withdrawPixKeyLookup: { loading: false, result: action.payload, error: null },
      };
    case actions.LOOKUP_WITHDRAW_PIX_KEY_ERROR:
      return {
        ...state,
        withdrawPixKeyLookup: { loading: false, result: null, error: action.payload },
      };
    case actions.LOOKUP_WITHDRAW_PIX_KEY_RESET:
      return {
        ...state,
        withdrawPixKeyLookup: { loading: false, result: null, error: null },
      };
    case actions.GET_WITHDRAW_KEY_HISTORY_REQUEST:
      return {
        ...state,
        fetchingWithdrawKeyHistory: true,
      };
    case actions.GET_WITHDRAW_KEY_HISTORY_SUCCESS:
      return {
        ...state,
        fetchingWithdrawKeyHistory: false,
        withdrawKeyHistory: action.payload,
      };
    case actions.GET_WITHDRAW_KEY_HISTORY_ERROR:
      return {
        ...state,
        fetchingWithdrawKeyHistory: false,
      };
    // Financial Transactions (Extrato)
    case actions.LIST_FINANCIAL_TRANSACTIONS_REQUEST:
      return {
        ...state,
        fetchingFinancialTransactions: true,
      };
    case actions.LIST_FINANCIAL_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        fetchingFinancialTransactions: false,
        financialTransactions: action.payload.data,
        financialTransactionsTotal: action.payload.totalCount,
      };
    case actions.LIST_FINANCIAL_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchingFinancialTransactions: false,
      };
    // Anticipations (Antecipação de Recebíveis)
    case actions.SIMULATE_ANTICIPATION_REQUEST:
      return {
        ...state,
        simulatingAnticipation: true,
        anticipationSimulation: null,
        anticipationSimulationError: null,
      };
    case actions.SIMULATE_ANTICIPATION_SUCCESS:
      return {
        ...state,
        simulatingAnticipation: false,
        anticipationSimulation: action.payload,
      };
    case actions.SIMULATE_ANTICIPATION_ERROR:
      return {
        ...state,
        simulatingAnticipation: false,
        anticipationSimulationError: action.payload,
      };
    case actions.SIMULATE_ANTICIPATION_CLEAR:
      return {
        ...state,
        anticipationSimulation: null,
        anticipationSimulationError: null,
      };
    case actions.REQUEST_ANTICIPATION_REQUEST:
      return {
        ...state,
        requestingAnticipation: true,
        requestAnticipationSuccess: false,
        requestAnticipationError: null,
      };
    case actions.REQUEST_ANTICIPATION_SUCCESS:
      return {
        ...state,
        requestingAnticipation: false,
        requestAnticipationSuccess: true,
      };
    case actions.REQUEST_ANTICIPATION_ERROR:
      return {
        ...state,
        requestingAnticipation: false,
        requestAnticipationError: action.payload,
      };
    case actions.LIST_ANTICIPATIONS_REQUEST:
      return {
        ...state,
        fetchingAnticipations: true,
      };
    case actions.LIST_ANTICIPATIONS_SUCCESS:
      return {
        ...state,
        fetchingAnticipations: false,
        anticipations: action.payload.data,
        anticipationsTotal: action.payload.totalCount,
      };
    case actions.LIST_ANTICIPATIONS_ERROR:
      return {
        ...state,
        fetchingAnticipations: false,
      };
    case actions.GET_ANTICIPATION_LIMITS_REQUEST:
      return {
        ...state,
        fetchingAnticipationLimits: true,
      };
    case actions.GET_ANTICIPATION_LIMITS_SUCCESS:
      return {
        ...state,
        fetchingAnticipationLimits: false,
        anticipationLimits: action.payload,
      };
    case actions.GET_ANTICIPATION_LIMITS_ERROR:
      return {
        ...state,
        fetchingAnticipationLimits: false,
      };
    case actions.CANCEL_ANTICIPATION_REQUEST:
      return {
        ...state,
        cancellingAnticipation: true,
      };
    case actions.CANCEL_ANTICIPATION_SUCCESS:
      return {
        ...state,
        cancellingAnticipation: false,
      };
    case actions.CANCEL_ANTICIPATION_ERROR:
      return {
        ...state,
        cancellingAnticipation: false,
      };
    default:
      return state;
  }
}
